export const dashboardRecentLeagueLimit = 3;

// G7-2: a cheap career-tenure figure for the archive cards. Only the tenure LENGTH
// is available without opening the league DB — the meta league record carries
// startingSeason (set once in updateMeta) and season (kept current by updatePhase),
// so "season N of this save" is a pure subtraction. Best record / championship
// counts would each need a heavy per-league read (out of scope), so they are
// reported as a degradation and not shown.
export const getCareerTenure = (league: {
	season?: number;
	startingSeason?: number;
}): number | undefined => {
	const { season, startingSeason } = league;
	if (
		typeof season !== "number" ||
		typeof startingSeason !== "number" ||
		season < startingSeason
	) {
		return undefined;
	}
	return season - startingSeason + 1;
};

export type DashboardLeagueState = {
	created?: Date;
	lastPlayed?: Date;
	lid: number;
};

export type DashboardEntryMode = "newUser" | "oneSave" | "multiSave";

const dateValue = (date: Date | undefined) => date?.getTime() ?? 0;

const compareLeagueRecency = (
	a: DashboardLeagueState,
	b: DashboardLeagueState,
) => {
	const lastPlayedDiff = dateValue(b.lastPlayed) - dateValue(a.lastPlayed);
	if (lastPlayedDiff !== 0) {
		return lastPlayedDiff;
	}

	const createdDiff = dateValue(b.created) - dateValue(a.created);
	if (createdDiff !== 0) {
		return createdDiff;
	}

	return b.lid - a.lid;
};

export const getDashboardLeagueState = <League extends DashboardLeagueState>(
	leagues: League[],
) => {
	const orderedLeagues = leagues.slice().sort(compareLeagueRecency);
	const recentLeagues = orderedLeagues.slice(0, dashboardRecentLeagueLimit);

	return {
		allLeagueCount: orderedLeagues.length,
		continueLeague: orderedLeagues[0],
		entryMode:
			orderedLeagues.length === 0
				? "newUser"
				: orderedLeagues.length === 1
					? "oneSave"
					: "multiSave",
		hasLibraryLeagues: orderedLeagues.length > 1,
		hasOverflowLeagues: orderedLeagues.length > dashboardRecentLeagueLimit,
		libraryLeagueCount: Math.max(0, orderedLeagues.length - 1),
		overflowLeagues: orderedLeagues.slice(dashboardRecentLeagueLimit),
		recentLeagues,
	};
};
